import {
  type Configuration,
  type PopupRequest,
  type RedirectRequest,
  ProtocolMode,
} from "@azure/msal-browser";

// JumpCloud OIDC issuer (see https://oauth.id.jumpcloud.com/.well-known/openid-configuration)
const DEFAULT_AUTHORITY = "https://oauth.id.jumpcloud.com/";

const getClientId = (): string => {
  if (window.runtimeConfig?.VITE_OIDC_CLIENT_ID) {
    return window.runtimeConfig.VITE_OIDC_CLIENT_ID;
  }
  const clientId = import.meta.env.VITE_OIDC_CLIENT_ID;
  if (!clientId) {
    throw new Error(
      "VITE_OIDC_CLIENT_ID is required. Please set it in your .env file.",
    );
  }
  return clientId;
};

const getAuthority = (): string => {
  if (window.runtimeConfig?.VITE_OIDC_AUTHORITY) {
    return window.runtimeConfig.VITE_OIDC_AUTHORITY;
  }
  return import.meta.env.VITE_OIDC_AUTHORITY || DEFAULT_AUTHORITY;
};

// MSAL requires non-Microsoft authorities to be explicitly trusted.
const getKnownAuthorities = (): string[] => {
  try {
    return [new URL(getAuthority()).host];
  } catch {
    return [];
  }
};

const getRedirectUri = (): string => {
  if (window.runtimeConfig?.VITE_OIDC_REDIRECT_URI) {
    return window.runtimeConfig.VITE_OIDC_REDIRECT_URI;
  }
  const redirectUri = import.meta.env.VITE_OIDC_REDIRECT_URI;
  if (redirectUri) {
    return redirectUri;
  }
  return window.location.origin;
};

const getPostLogoutRedirectUri = (): string => {
  if (window.runtimeConfig?.VITE_OIDC_POST_LOGOUT_REDIRECT_URI) {
    return window.runtimeConfig.VITE_OIDC_POST_LOGOUT_REDIRECT_URI;
  }
  const postLogoutUri = import.meta.env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI;
  if (postLogoutUri) {
    return postLogoutUri;
  }
  return `${window.location.origin}/auth/login`;
};

export const msalConfig: Configuration = {
  auth: {
    clientId: getClientId(),
    authority: getAuthority(),
    knownAuthorities: getKnownAuthorities(),
    protocolMode: ProtocolMode.OIDC,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

export const loginRequest: PopupRequest | RedirectRequest = {
  scopes: ["openid", "profile", "email", "offline_access"],
};
